import type { FunStyle } from "@/lib/savedMoment/funStyle";
import { testIds } from "@/testIds";

/**
 * The Fun styles' art — PLACEHOLDERS, drawn to time and size the moment.
 *
 * Olivia's handoff §4: commission Lottie or sprite frames, 64 × 64, in the
 * product's palette only (cerulean, success green, neutrals), readable at
 * 32 px. Each one here is a 64 × 64 SVG in tokens only, so the timing,
 * placement and reduced-motion still frame can ship now and the art can be
 * swapped in later without touching the moment.
 *
 * Every animated part carries a class the stylesheet times; the SVGs hold no
 * timing of their own.
 */

/** Lili: a page with a folded corner and a face. `wave` is her one extra
 *  pose, for back online. */
function Lili({ wave }: { wave: boolean }) {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden focusable="false">
      {/* the page */}
      <path
        d="M14 6 H42 L54 18 V56 a4 4 0 0 1 -4 4 H14 a4 4 0 0 1 -4 -4 V10 a4 4 0 0 1 4 -4 Z"
        fill="var(--lilia-bg)"
        stroke="var(--lilia-primary)"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* folded corner */}
      <path d="M42 6 V14 a4 4 0 0 0 4 4 H54 Z" fill="var(--lilia-primary-subtle)" stroke="var(--lilia-primary)" strokeWidth="3" strokeLinejoin="round" />
      {/* eyes: they blink */}
      <g className="si-art-blink">
        <rect x="21" y="27" width="5" height="8" rx="2.5" fill="var(--lilia-text)" />
        <rect x="37" y="27" width="5" height="8" rx="2.5" fill="var(--lilia-text)" />
      </g>
      {/* cheeks, in the accent rather than a new colour */}
      <ellipse cx="18" cy="40" rx="4" ry="2.2" fill="var(--lilia-primary)" opacity="0.28" />
      <ellipse cx="45" cy="40" rx="4" ry="2.2" fill="var(--lilia-primary)" opacity="0.28" />
      {/* smile */}
      <path d="M27 41 q4.5 4 9 0" fill="none" stroke="var(--lilia-text)" strokeWidth="2.4" strokeLinecap="round" />
      {wave ? (
        <g className="si-art-wave">
          <path d="M54 34 q7 -4 7 -13" fill="none" stroke="var(--lilia-primary)" strokeWidth="3" strokeLinecap="round" />
          <circle cx="61" cy="19" r="3" fill="var(--lilia-bg)" stroke="var(--lilia-primary)" strokeWidth="2.4" />
        </g>
      ) : (
        /* the ✓ badge */
        <g className="si-art-badge">
          <circle cx="52" cy="52" r="10" fill="var(--lilia-success)" stroke="var(--lilia-bg)" strokeWidth="3" />
          <path d="M47.5 52.5 l3 3 l6 -6.5" fill="none" stroke="var(--lilia-save-tick)" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
    </svg>
  );
}

/** The safe: drops in open, the door shuts, the dial turns (its face warm
 *  while it turns), the lamp goes green. */
function Safe() {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden focusable="false">
      {/* feet */}
      <rect x="12" y="54" width="10" height="6" rx="2" fill="var(--lilia-text-secondary)" />
      <rect x="42" y="54" width="10" height="6" rx="2" fill="var(--lilia-text-secondary)" />
      {/* body and the dark inside you see while the door is open */}
      <rect x="6" y="6" width="52" height="50" rx="7" fill="var(--lilia-text-secondary)" />
      <rect x="11" y="11" width="42" height="40" rx="4" fill="var(--lilia-bg-secondary)" />
      {/* the door swings shut on its left hinge */}
      <g className="si-art-door">
        <rect x="10" y="10" width="44" height="42" rx="5" fill="var(--lilia-text-muted)" stroke="var(--lilia-text-secondary)" strokeWidth="2" />
        <g className="si-art-dial">
          {/* The dial's face: the door's own neutral at rest. It fills with
              --lilia-accent-warm only while it turns to lock, then eases back
              (warm accent §3; timed in saveIndicator.css). The body stays
              neutral and the lamp stays green: gold means "locked away". */}
          <circle
            className="si-art-dial-face"
            data-testid={testIds.editor.saveIndicatorSafeDial}
            cx="32"
            cy="31"
            r="9"
            fill="var(--lilia-text-muted)"
            stroke="var(--lilia-bg)"
            strokeWidth="3"
          />
          <rect x="30.5" y="22" width="3" height="7" rx="1.5" fill="var(--lilia-bg)" />
        </g>
      </g>
      {/* the lamp */}
      <circle className="si-art-lamp" cx="49" cy="15" r="3.5" />
    </svg>
  );
}

/** The stamp: a rubber SAVED at −9°, on the indicator, then gone. */
function Stamp() {
  return (
    <span className="si-art-stamp-face" aria-hidden>
      SAVED
    </span>
  );
}

export function FunArt({ style, momentId }: { style: FunStyle; momentId: number }) {
  if (style === "stamp") {
    return (
      <span key={momentId} className="si-fun si-fun--stamp" data-testid={testIds.editor.saveIndicatorFun} data-fun-style={style}>
        <Stamp />
      </span>
    );
  }
  return (
    // The clip is the top bar's lower edge: the art slides out from under it,
    // into the gutter beside the title, and never across the canvas column.
    <span className="si-fun-clip" aria-hidden>
      <span key={momentId} className={`si-fun si-fun--drop si-fun--${style}`} data-testid={testIds.editor.saveIndicatorFun} data-fun-style={style}>
        {style === "safe" ? <Safe /> : <Lili wave={style === "lili-wave"} />}
      </span>
    </span>
  );
}
